import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

export interface AssistantMessage {
  id?: number | null;
  content: string;
  role: 'user' | 'assistant';
  created_at?: string;
}

/** Something the doctor offers to save; written only when the user confirms it. */
export interface Proposal {
  token: string;
  kind: 'vital' | 'reminder';
  summary: string;
}

export interface ChatResponse {
  assistant_message: AssistantMessage;
  conversation_id: number;
  user_message: AssistantMessage;
  proposals: Proposal[];
  consultation: ConsultationAdvice | null;
  emergency: boolean;
  ai_available: boolean;
}

export interface ConsentStatus {
  granted: boolean;
  service_enabled: boolean;
  policy_version: string;
}

export interface Conversation {
  id: number;
  title?: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
  last_message?: {
    content?: string;
    role?: string;
    created_at?: string;
  };
}

export interface ConversationDetail {
  id: number;
  title?: string;
  messages: AssistantMessage[];
  created_at: string;
}

export interface ProactiveInsight {
  content: string;
  title?: string;
}

export interface DocumentVitalRecord {
  type: 'update_vital';
  vital: string;
  value?: string;
  systolic?: string;
  diastolic?: string;
  unit: string;
}

export interface DocumentExtractionResponse {
  review_token: string;
  records: DocumentVitalRecord[];
  message: string;
}

export interface RecommendedTest {
  name: string;
  reason?: string;
}

export interface ConsultationSpecialization {
  id: number;
  name: string;
  slug: string;
}

/** The AI Family Doctor's advice about whether a specialist consult is needed. */
export interface ConsultationAdvice {
  recommended: boolean;
  urgency: 'routine' | 'soon' | 'urgent';
  specializations: ConsultationSpecialization[];
  reason?: string;
}

/** Primary-care / general-practice slugs (mirrors the backend PRIMARY_CARE_SLUGS). */
const PRIMARY_CARE_SLUGS = [
  'general-physician', 'general-physician-consultation', 'general-practitioner',
  'general-practice', 'general-medicine', 'family-medicine', 'family-physician',
  'primary-care', 'general', 'gp',
];

/** True when the only offered specialties are primary care (the GP fallback). */
export function isPrimaryCareOnly(specializations: ConsultationSpecialization[]): boolean {
  return specializations.length > 0
    && specializations.every((s) => PRIMARY_CARE_SLUGS.includes(s.slug));
}

export interface DocumentAnalysisResponse {
  report_id: number;
  conversation_id?: number;
  user_message?: AssistantMessage;
  assistant_message?: AssistantMessage;
  review_token: string;
  records: DocumentVitalRecord[];
  recommended_tests: RecommendedTest[];
  consultation: ConsultationAdvice;
  summary: string;
  emergency: boolean;
  emergency_advice: string;
  message: string;
}

const ha = API_ENDPOINTS.healthAssistant;

const healthAssistantService = {
  getConsent: async (): Promise<ConsentStatus> => (await axiosInstance.get(ha.consent)).data,

  grantConsent: async (): Promise<void> => {
    await axiosInstance.post(ha.consent);
  },

  /** Withdraws consent; the server also deletes every consultation. */
  withdrawConsent: async (): Promise<void> => {
    await axiosInstance.delete(ha.consent);
  },

  sendMessage: async (message: string, language: string, conversationId?: number): Promise<ChatResponse> => {
    const payload: Record<string, unknown> = { message, language };
    if (conversationId) payload.conversation_id = conversationId;
    return (await axiosInstance.post(ha.chat, payload)).data;
  },

  confirmProposal: async (token: string): Promise<{ success: boolean; message: string }> =>
    (await axiosInstance.post(ha.confirmAction, { token })).data,

  getConversations: async (): Promise<Conversation[]> => {
    const d = (await axiosInstance.get(ha.conversations)).data;
    return Array.isArray(d) ? d : d.results || [];
  },

  getConversationMessages: async (id: number): Promise<ConversationDetail> =>
    (await axiosInstance.get(ha.conversationDetail(id))).data,

  deleteConversation: async (id: number): Promise<void> => {
    await axiosInstance.delete(ha.conversationDetail(id));
  },

  getProactiveInsight: async (language: string): Promise<ProactiveInsight> =>
    (await axiosInstance.get(ha.insight, { params: { language } })).data,

  /** MP3 of one of the doctor's replies, spoken in a natural Indian male voice. */
  speak: async (messageId: number): Promise<Blob> =>
    (await axiosInstance.post(ha.speak, { message_id: messageId }, { responseType: 'blob' })).data,

  transcribe: async (audio: Blob, language: string): Promise<string> => {
    const data = new FormData();
    const ext = audio.type.includes('mp4') ? 'mp4' : audio.type.includes('ogg') ? 'ogg' : 'webm';
    data.append('audio', audio, `speech.${ext}`);
    data.append('language', language);
    const res = await axiosInstance.post(ha.transcribe, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.text || '';
  },

  extractDocument: async (file: File): Promise<DocumentExtractionResponse> => {
    const data = new FormData();
    data.append('document', file);
    const response = await axiosInstance.post(ha.documentExtract, data, {
      // axiosInstance defaults all requests to JSON; override it for the file upload.
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Read a report and return everything the AI Family Doctor can offer:
   * vital readings to save, recommended tests, and consultation advice. The
   * returned `report_id` can be attached to a teleconsultation booking.
   */
  analyzeDocument: async (file: File, language?: string, conversationId?: number): Promise<DocumentAnalysisResponse> => {
    const data = new FormData();
    data.append('document', file);
    if (language) data.append('language', language);
    if (conversationId) data.append('conversation_id', String(conversationId));
    const response = await axiosInstance.post(ha.documentAnalyze, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  confirmDocumentExtraction: async (reviewToken: string, records: DocumentVitalRecord[]) =>
    (await axiosInstance.post(ha.documentConfirm, { review_token: reviewToken, records })).data,
};

export default healthAssistantService;
